import { getCanonicalDomain } from "@guardian/shared-utils";
import { extractBrandSlug } from "@/lib/branding";

// WHY: SPA must not trust the wire — re-validate brandSlug before interpolating into
// a redirect URL, mirroring BrandingProvider's hasRedirectFields guard. The DB CHECK
// constraint prevents arbitrary values today, but a future DB-bypass path that wrote
// brandSlug="evil.com?" would interpolate to https://evil.com?.gcheckin.com/... (open redirect).
const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;

// WHY: reconcileBrandHost issues a *hard* window.location.replace. A single redirect
// always suffices to reach the correct host, so a SECOND, opposite hard redirect for
// the same path in the same tab session is the reverse↔forward ping-pong loop (each
// hop unloads the document → blank page). A transient-null brandSlug during a
// same-path guest-nav re-init can start that loop. This one-shot guard, keyed by the
// (host-preserving) pathname, lets each path redirect at most once per session —
// suppressing only the pathological bounce-back, never the normal single redirect.
const REDIRECT_GUARD_KEY = "gci_brand_redirect_guard";

function replaceHostOnce(target: string): void {
  try {
    if (sessionStorage.getItem(REDIRECT_GUARD_KEY) === window.location.pathname) {
      return; // already performed a brand-host redirect for this path this session
    }
    sessionStorage.setItem(REDIRECT_GUARD_KEY, window.location.pathname);
  } catch {
    // sessionStorage unavailable (private mode / disabled) — fall through and
    // redirect; the reverse-gate + ready gate remain the primary loop defenses.
  }
  window.location.replace(target);
}

/**
 * Reconciles the SPA hostname with a public resource's brandSlug in both directions.
 * Shared by FormDataBrandRedirect (form route) and useListingBrandRedirect (listing route).
 *
 * FORWARD (canonical/legacy → branded): on the canonical/legacy host, when the resource
 * qualifies for brand application (non-null brandSlug = active Business-tier brand) AND
 * the owner has auto-redirect enabled, redirects to the branded subdomain preserving
 * path + search.
 *
 * REVERSE (branded → canonical): on a branded subdomain whose slug is absent from the
 * wire (per-listing tier gate suppressed it) or does NOT match the current subdomain
 * (stale cross-Owner link), redirects to the canonical domain.
 *
 * @param brandAutoRedirect boolean | null — NOT coerced to a default; a null wire value
 *   is always paired with a null brandSlug, so the `!brandSlug` guard returns first and
 *   the `=== false` suppression only fires on an explicit owner opt-out.
 * @param ready load-settled gate. Until true the function no-ops, preventing the
 *   reverse↔forward redirect cycle that would arise from acting on a pre-fetch null slug.
 */
export function reconcileBrandHost(args: {
  brandSlug: string | null;
  brandAutoRedirect: boolean | null;
  ready: boolean;
}): void {
  const { brandSlug, brandAutoRedirect, ready } = args;
  if (!ready) return;

  const canonicalDomain = getCanonicalDomain();
  const currentSlug = extractBrandSlug(window.location.hostname);

  if (currentSlug) {
    if (!brandSlug || brandSlug !== currentSlug) {
      const canonical = `https://${canonicalDomain}${window.location.pathname}${window.location.search}`;
      replaceHostOnce(canonical);
    }
    return;
  }

  if (!brandSlug) return;
  if (brandAutoRedirect === false) return;
  if (!SLUG_PATTERN.test(brandSlug)) return;
  const branded = `https://${brandSlug}.${canonicalDomain}${window.location.pathname}${window.location.search}`;
  replaceHostOnce(branded);
}
